/* eslint-disable consistent-return */
import * as React from 'react'
import { Box, Grid, Typography, useMediaQuery } from '@mui/material'
import Image from 'next/image'
import PropTypes from 'prop-types'
import { makeStyles } from 'tss-react/mui'
import Button from 'src/common/components/button/Button'
import { rem } from 'src/common/utils/css'
import sanitize from 'src/common/utils/js/sanitize'
import {
  VERSION_TYPES,
  getGridColsWidth,
} from 'src/components/text-and-map/helpers'
import theme, { FONTS } from 'src/styles/theme'
import { useLinkClickHandler } from 'src/common/utils/hooks/useLinkClickHandler'
import isUrlExternal from 'src/common/utils/js/isUrlExternal'
import { ButtonExternalLink } from 'src/common/components/externalSiteIcon'
import { min } from 'date-fns'

/** Link-style back control defined at module scope (lint-safe). */
const BackLink = React.forwardRef(
  ({ onClick, children, ariaLabel, className }, ref) => (
    <button
      type="button"
      onClick={onClick}
      ref={ref}
      className={className}
      aria-label={
        ariaLabel || (typeof children === 'string' ? children : 'Back')
      }
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M9.17475 3.34088C9.50425 3.67038 9.50425 4.20462 9.17475 4.53412L4.70887 9L9.17475 13.4659C9.50425 13.7954 9.50425 14.3296 9.17475 14.6591C8.84524 14.9886 8.31101 14.9886 7.9815 14.6591L2.919 9.59662C2.5895 9.26712 2.5895 8.73288 2.919 8.40338L7.9815 3.34088C8.31101 3.01137 8.84524 3.01137 9.17475 3.34088Z"
          fill="#1F4D25"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3.375 9C3.375 8.53401 3.75276 8.15625 4.21875 8.15625H14.4844C14.9504 8.15625 15.3281 8.53401 15.3281 9C15.3281 9.46599 14.9504 9.84375 14.4844 9.84375H4.21875C3.75276 9.84375 3.375 9.46599 3.375 9Z"
          fill="#1F4D25"
        />
      </svg>
      <span>{children}</span>
    </button>
  )
)

BackLink.displayName = 'BackLink'

/* =========================
   Google Maps helpers (module scope)
   ========================= */
const GOOGLE_MAPS_API_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
const GOOGLE_MAP_ID = process.env.NEXT_PUBLIC_GOOGLE_MAP_ID // optional Cloud Map Style ID

const MAP_MIN_ZOOM_DESKTOP = 14
const MAP_MIN_ZOOM_MOBILE = 13.25
const MAP_MAX_ZOOM = 17.5

// Custom marker SVGs
const MARKER_SVG_DEFAULT = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 67 80" fill="none">
  <g filter="url(#filter0_d_18_122)">
    <ellipse cx="32.938" cy="32.8023" rx="8.4375" ry="8.37209" fill="#F9F1DB"/>
    <path d="M33.4585 15.5C42.4174 15.5002 52.7547 23.0412 51.3765 35.041C50.8678 39.4693 47.9306 48.7554 33.4585 63.5C27.7155 57.9 16.091 44.3687 15.5396 35.043C14.8301 23.043 23.8102 15.5 33.4585 15.5ZM33.0737 24.6133C28.4141 24.6135 24.6364 28.3619 24.6362 32.9854C24.6362 37.609 28.414 41.3572 33.0737 41.3574C37.7336 41.3574 41.5112 37.6091 41.5112 32.9854C41.5111 28.3617 37.7335 24.6133 33.0737 24.6133Z" fill="#CD4F30"/>
    <path d="M33.4585 15.5L33.4585 15H33.4585V15.5ZM51.3765 35.041L51.8732 35.0981V35.0981L51.3765 35.041ZM33.4585 63.5L33.1094 63.858L33.4662 64.2059L33.8153 63.8502L33.4585 63.5ZM15.5396 35.043L15.0404 35.0725L15.0404 35.0725L15.5396 35.043ZM33.0737 24.6133V24.1133H33.0737L33.0737 24.6133ZM24.6362 32.9854L24.1362 32.9853V32.9854H24.6362ZM33.0737 41.3574L33.0737 41.8574H33.0737V41.3574ZM41.5112 32.9854H42.0112V32.9853L41.5112 32.9854ZM33.4585 15.5L33.4585 16C42.1998 16.0002 52.2153 23.3561 50.8797 34.984L51.3765 35.041L51.8732 35.0981C53.2942 22.7262 42.6349 15.0002 33.4585 15L33.4585 15.5ZM51.3765 35.041L50.8797 34.984C50.3882 39.2636 47.5277 48.4521 33.1017 63.1498L33.4585 63.5L33.8153 63.8502C48.3335 49.0588 51.3475 39.675 51.8732 35.0981L51.3765 35.041ZM33.4585 63.5L33.8076 63.142C30.9542 60.3597 26.6334 55.6001 22.9611 50.3809C19.2725 45.1385 16.3054 39.5247 16.0387 35.0135L15.5396 35.043L15.0404 35.0725C15.3251 39.8869 18.446 45.7016 22.1433 50.9564C25.8569 56.2343 30.2198 61.0403 33.1094 63.858L33.4585 63.5ZM15.5396 35.043L16.0387 35.0135C15.3477 23.3257 24.0723 16 33.4585 16V15.5V15C23.5481 15 14.3125 22.7603 15.0404 35.0725L15.5396 35.043ZM33.0737 24.6133L33.0737 24.1133C28.1417 24.1136 24.1364 28.082 24.1362 32.9853L24.6362 32.9854L25.1362 32.9854C25.1364 28.6417 28.6866 25.1135 33.0738 25.1133L33.0737 24.6133ZM24.6362 32.9854H24.1362C24.1362 37.8888 28.1416 41.8572 33.0737 41.8574L33.0737 41.3574L33.0738 40.8574C28.6865 40.8572 25.1362 37.3291 25.1362 32.9854H24.6362ZM33.0737 41.3574V41.8574C38.0061 41.8574 42.0112 37.8889 42.0112 32.9854H41.5112H41.0112C41.0112 37.3293 37.4612 40.8574 33.0737 40.8574V41.3574ZM41.5112 32.9854L42.0112 32.9853C42.0111 28.0819 38.006 24.1133 33.0737 24.1133V24.6133V25.1133C37.4611 25.1133 41.0111 28.6415 41.0112 32.9854L41.5112 32.9854Z" fill="#F9F1DB"/>
  </g>
  <defs>
    <filter id="filter0_d_18_122" x="0" y="0" width="67.0015" height="79.2059" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
      <feOffset/>
      <feGaussianBlur stdDeviation="7.5"/>
      <feComposite in2="hardAlpha" operator="out"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_18_122"/>
      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_18_122" result="shape"/>
    </filter>
  </defs>
</svg>
`

const MARKER_SVG_ACTIVE = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 67 80" fill="none">
  <g filter="url(#filter0_d_18_122)">
    <ellipse cx="32.938" cy="32.8023" rx="8.4375" ry="8.37209" fill="#F9F1DB"/>
    <path d="M33.4585 15.5C42.4174 15.5002 52.7547 23.0412 51.3765 35.041C50.8678 39.4693 47.9306 48.7554 33.4585 63.5C27.7155 57.9 16.091 44.3687 15.5396 35.043C14.8301 23.043 23.8102 15.5 33.4585 15.5ZM33.0737 24.6133C28.4141 24.6135 24.6364 28.3619 24.6362 32.9854C24.6362 37.609 28.414 41.3572 33.0737 41.3574C37.7336 41.3574 41.5112 37.6091 41.5112 32.9854C41.5111 28.3617 37.7335 24.6133 33.0737 24.6133Z" fill="#8A260D"/>
    <path d="M33.4585 15.5L33.4585 15H33.4585V15.5ZM51.3765 35.041L51.8732 35.0981V35.0981L51.3765 35.041ZM33.4585 63.5L33.1094 63.858L33.4662 64.2059L33.8153 63.8502L33.4585 63.5ZM15.5396 35.043L15.0404 35.0725L15.0404 35.0725L15.5396 35.043ZM33.0737 24.6133V24.1133H33.0737L33.0737 24.6133ZM24.6362 32.9854L24.1362 32.9853V32.9854H24.6362ZM33.0737 41.3574L33.0737 41.8574H33.0737V41.3574ZM41.5112 32.9854H42.0112V32.9853L41.5112 32.9854ZM33.4585 15.5L33.4585 16C42.1998 16.0002 52.2153 23.3561 50.8797 34.984L51.3765 35.041L51.8732 35.0981C53.2942 22.7262 42.6349 15.0002 33.4585 15L33.4585 15.5ZM51.3765 35.041L50.8797 34.984C50.3882 39.2636 47.5277 48.4521 33.1017 63.1498L33.4585 63.5L33.8153 63.8502C48.3335 49.0588 51.3475 39.675 51.8732 35.0981L51.3765 35.041ZM33.4585 63.5L33.8076 63.142C30.9542 60.3597 26.6334 55.6001 22.9611 50.3809C19.2725 45.1385 16.3054 39.5247 16.0387 35.0135L15.5396 35.043L15.0404 35.0725C15.3251 39.8869 18.446 45.7016 22.1433 50.9564C25.8569 56.2343 30.2198 61.0403 33.1094 63.858L33.4585 63.5ZM15.5396 35.043L16.0387 35.0135C15.3477 23.3257 24.0723 16 33.4585 16V15.5V15C23.5481 15 14.3125 22.7603 15.0404 35.0725L15.5396 35.043ZM33.0737 24.6133L33.0737 24.1133C28.1417 24.1136 24.1364 28.082 24.1362 32.9853L24.6362 32.9854L25.1362 32.9854C25.1364 28.6417 28.6866 25.1135 33.0738 25.1133L33.0737 24.6133ZM24.6362 32.9854H24.1362C24.1362 37.8888 28.1416 41.8572 33.0737 41.8574L33.0737 41.3574L33.0738 40.8574C28.6865 40.8572 25.1362 37.3291 25.1362 32.9854H24.6362ZM33.0737 41.3574V41.8574C38.0061 41.8574 42.0112 37.8889 42.0112 32.9854H41.5112H41.0112C41.0112 37.3293 37.4612 40.8574 33.0737 40.8574V41.3574ZM41.5112 32.9854L42.0112 32.9853C42.0111 28.0819 38.006 24.1133 33.0737 24.1133V24.6133V25.1133C37.4611 25.1133 41.0111 28.6415 41.0112 32.9854L41.5112 32.9854Z" fill="#F9F1DB"/>
  </g>
  <defs>
    <filter id="filter0_d_18_122" x="0" y="0" width="67.0015" height="79.2059" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
      <feFlood flood-opacity="0" result="BackgroundImageFix"/>
      <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
      <feOffset/>
      <feGaussianBlur stdDeviation="7.5"/>
      <feComposite in2="hardAlpha" operator="out"/>
      <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
      <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_18_122"/>
      <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_18_122" result="shape"/>
    </filter>
  </defs>
</svg>
`

const RAW_GEOJSON_COORDS = [
  [-122.47320105664052, 37.78721660548222],
  [-122.47100432190123, 37.787234062358436],
  [-122.44647397402353, 37.79214151726552],
  [-122.44788704698945, 37.80135890790842],
  [-122.44914545151843, 37.801766781632736],
  [-122.44983301025975, 37.80275989930471],
  [-122.44926599715446, 37.80416800463181],
  [-122.44839841159059, 37.80459236526121],
  [-122.44872848182982, 37.80670159837632],
  [-122.45165410203619, 37.806352900222045],
  [-122.45448347611784, 37.806469137180315],
  [-122.45839436009298, 37.805481831368155],
  [-122.46357930812047, 37.80487981539589],
  [-122.46720353761089, 37.80601223034267],
  [-122.47053667576192, 37.80858248560048],
  [-122.47480154464486, 37.80917933095321],
  [-122.4760486272011, 37.80963984307043],
  [-122.47639890374838, 37.81075785121297],
  [-122.47773818038137, 37.81094296744406],
  [-122.47789620306555, 37.80844904353552],
  [-122.47840512957187, 37.80809330830708],
  [-122.47872908698903, 37.805492611321895],
  [-122.47989898314036, 37.80289755888889],
  [-122.48020230905269, 37.801416965889224],
  [-122.48097424630762, 37.80033090940849],
  [-122.48436408721982, 37.79273978948222],
  [-122.48598078755872, 37.790803460511135],
  [-122.48505250784541, 37.790367992302684],
  [-122.48370790142899, 37.78877712013687],
  [-122.4840621581383, 37.78727728124956],
  [-122.4809729782636, 37.787025252587824],
  [-122.47717916601519, 37.78701662508435],
  [-122.47454214823001, 37.786790577621325],
  [-122.47320105664052, 37.78721660548222],
]

const DASHED_BOUNDARY_PATH = RAW_GEOJSON_COORDS.map(([lng, lat]) => ({
  lat,
  lng,
}))

const MARKER_ICON_DEFAULT_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  MARKER_SVG_DEFAULT
)}`
const MARKER_ICON_ACTIVE_URL = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  MARKER_SVG_ACTIVE
)}`

// Put this above loadGoogleMaps (or keep it if you already have it)
let googleMapsLoaderPromise = null

async function loadGoogleMaps() {
  if (typeof window === 'undefined') return null

  if (!GOOGLE_MAPS_API_KEY) {
    console.error(
      'Google Maps: NEXT_PUBLIC_GOOGLE_MAPS_API_KEY is not set. Map will not be initialized.'
    )
    return null
  }

  // If the classic global Map constructor already exists, use it immediately.
  if (
    window.google?.maps?.Map &&
    typeof window.google.maps.Map === 'function'
  ) {
    return window.google.maps
  }

  // Reuse a single in-flight loader promise across all calls.
  if (googleMapsLoaderPromise) {
    try {
      return await googleMapsLoaderPromise
    } catch (err) {
      console.error(err)
      return null
    }
  }

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const resolveIfReady = () => {
      if (
        window.google?.maps?.Map &&
        typeof window.google.maps.Map === 'function'
      ) {
        resolve(window.google.maps)
        return true
      }
      return false
    }

    const finishAfterLoad = async () => {
      try {
        const gmaps = window.google?.maps

        // Use modern importLibrary *if* available, but don't require it.
        if (gmaps?.importLibrary) {
          try {
            await gmaps.importLibrary('maps')
          } catch (e) {
            // If importLibrary fails, we'll still try to use the classic Map.
            console.warn(
              'Google Maps: importLibrary("maps") failed, falling back to classic Map.',
              e
            )
          }
        }

        if (!resolveIfReady()) {
          reject(
            new Error('Google Maps: Map constructor missing after script load.')
          )
        }
      } catch (e) {
        reject(e)
      }
    }

    // If script already exists, hook into its load/error events.
    const existingScript = document.querySelector('script[data-gmaps-loader]')
    if (existingScript) {
      // In case the script has already loaded, try resolving immediately.
      if (resolveIfReady()) return

      existingScript.addEventListener('load', finishAfterLoad)
      existingScript.addEventListener('error', () => {
        reject(
          new Error(
            'Google Maps JavaScript API failed to load (existing script).'
          )
        )
      })
      return
    }

    // Create a single script tag (modern params but compatible with classic API).
    const script = document.createElement('script')
    const params = new URLSearchParams({
      key: GOOGLE_MAPS_API_KEY,
      v: 'weekly',
      libraries: 'maps',
      // no callback: we control init ourselves
    })

    script.src = `https://maps.googleapis.com/maps/api/js?${params.toString()}`
    script.async = true
    script.defer = true
    script.setAttribute('data-gmaps-loader', '1')

    script.addEventListener('load', finishAfterLoad)
    script.addEventListener('error', () => {
      reject(new Error('Google Maps JavaScript API failed to load'))
    })

    document.head.appendChild(script)
  })

  try {
    return await googleMapsLoaderPromise
  } catch (err) {
    console.error(err)
    // Allow future retries if this load attempt failed.
    googleMapsLoaderPromise = null
    return null
  }
}

const backgroundColor = 'baker_beach'

const useStyles = makeStyles()((defaultTheme, { isFiftyFifty, reverse }) => ({
  container: {
    padding: `${rem(40)} 0 0`,
    color: theme.palette.presidio.color.DARK_GRAY,
    backgroundColor: theme.palette.presidio.color.LIGHT_BACKGROUND,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    flexWrap: 'nowrap',
    gap: rem(40),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'column',
      padding: `${rem(40)} 0 0`,
    },
    [theme.breakpoints.up('lg')]: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'stretch',
      gap: rem(24),
      padding: '0',
    },
    [theme.breakpoints.up('xl')]: {
      padding: `${rem(0)}`,
      gap: isFiftyFifty ? rem(120) : rem(24),
    },
    position: 'relative',
    overflow: 'clip',
  },
  paddedBox: {
    paddingRight: rem(16),
    paddingLeft: rem(16),
    [theme.breakpoints.up('lg')]: {
      paddingTop: rem(40),
      paddingBottom: rem(40),
    },
    [theme.breakpoints.up('xl')]: {
      paddingTop: rem(54),
      paddingBottom: rem(54),
      paddingLeft: `${rem(156)}`,
    },
  },
  marginBox: {
    margin: `${rem(54)} 0`,
  },
  marginTop: {
    marginTop: `${rem(54)}`,
  },
  textBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: rem(24),
    [theme.breakpoints.up('md')]: {
      alignItems: 'start',
      gap: rem(24),
    },
    zIndex: 10,
  },
  heading: {
    color: theme.palette.primary.dark,
    marginBottom: '8px',
  },
  subHeading: {
    color: theme.palette.primary.dark,
    marginBottom: '32px',
  },
  listItemContainer: {
    ...theme.typography.body.default,
    background: theme.palette.presidio.color.BAKER_BEACH_WHITE,
    borderRadius: '4px',
    padding: '24px',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      columnCount: 2,
      columnFill: 'balance',
    },
  },
  singleSection: {
    breakInside: 'avoid',
    WebkitColumnBreakInside: 'avoid',
    pageBreakInside: 'avoid',
    display: 'inline-block',
    width: '100%',
    marginBottom: rem(16),
  },
  listItemBox: {
    ...theme.typography.body.default,
    paddingLeft: 0,
    margin: 0,
  },
  listItemLi: {
    listStyle: 'none',
    breakInside: 'avoid',
    marginBottom: rem(8),
  },
  listItemButton: {
    ...theme.typography.body.inlineLink,
    padding: 0,
    margin: 0,
    border: 'none',
    cursor: 'pointer',
    color: theme.palette.primary.dark,
  },
  listItemHeading: {
    ...theme.typography.body.default,
    color: theme.palette.presidio.color.BLACK,
    fontFamily: FONTS.ROC_GROTESK,
    fontWeight: 500,
    textTransform: 'uppercase',
    fontSize: '18px',
    lineHeight: 1.5,
    marginBottom: '8px',
    breakInside: 'avoid',
  },

  cardDialog: {
    width: '100%',
  },
  cardBox: {
    background: theme.palette.presidio.color.BAKER_BEACH_WHITE,
    borderRadius: '4px',
    padding: '24px',
    width: '100%',
  },
  cardTitle: {
    color: theme.palette.primary.dark,
    fontSize: '24px',
    marginBottom: '24px',
  },
  cardImage: {
    borderRadius: '8px',
    marginBottom: '24px',
  },
  cardDescription: {
    ...theme.typography.body.default,
    marginTop: '24px',
  },
  cardCta: {
    display: 'block',
    marginTop: '24px',
  },
  mapBox: {
    height: '100%',
  },

  /* style hook for BackLink */
  backLink: {
    alignItems: 'center',
    appearance: 'none',
    WebkitAppearance: 'none',
    background: 'none',
    border: 'none',
    color: theme.palette.primary.dark,
    display: 'flex',
    fontFamily: FONTS.ROC_GROTESK,
    fontWeight: 500,
    gap: '8px',
    textTransform: 'uppercase',
    fontSize: '18px',
    lineHeight: 1,
    margin: '0 0 24px',
    padding: 0,
    '&:hover': {
      cursor: 'pointer',
      color: '#517843',
      textDecoration: 'underline',
    },
  },

  /* off-screen measurement root */
  measureRoot: {
    position: 'absolute',
    left: -99999,
    top: 0,
    visibility: 'hidden',
    overflow: 'hidden',
  },
}))

// For inset & shape media frames only
export default function TextAndMap(props) {
  const { data } = props
  if (!data || Object.keys(data).length <= 0) return null

  const { section_id, text_map: textMap } = data
  if (!textMap || Object.keys(textMap).length <= 0) return null

  const {
    ctaHeading: heading,
    tag_selection: tagSelection = 'h2',
    style_selection: styleSelection = 'h2',
    ctaSubheading: subHeading,
    versions,
    list_sections: listSections,
    back_button_text: backBtn,
    buttonStyle = 'primary',
    media_position: mediaPosition,
  } = textMap

  const isFiftyFifty = true
  const isTextDominant = versions === VERSION_TYPES.text_dominant
  const reverse = mediaPosition === 'left'

  const { textPart, mediaPart } = getGridColsWidth(isFiftyFifty, isTextDominant)
  const { classes } = useStyles({
    backgroundColor,
    isFiftyFifty,
    reverse,
  })

  const linkClickHandler = useLinkClickHandler()

  // Only apply min-height behavior at >= 1024px
  const isDesktop = useMediaQuery('(min-width:1024px)')
  const minZoom = isDesktop ? MAP_MIN_ZOOM_DESKTOP : MAP_MIN_ZOOM_MOBILE

  // ===== Overlay state =====
  const [activeItem, setActiveItem] = React.useState(null)
  const lastTriggerRef = React.useRef(null)

  const mapDivRef = React.useRef(null)
  const mapRef = React.useRef(null) // google.maps.Map
  const markersRef = React.useRef([]) // [{ marker, item }]
  const gmapsRef = React.useRef(null)
  const initialBoundsRef = React.useRef(null)

  // custom overlay (OverlayView) for gray/red pill
  const overlayRef = React.useRef(null) // { overlay, div }

  // dashed boundary polyline (like Google Maps place boundary)
  const boundaryPolylineRef = React.useRef(null)

  // refs to share marker icons + active item between effects & listeners
  const markerIconDefaultRef = React.useRef(null)
  const markerIconActiveRef = React.useRef(null)
  const activeItemRef = React.useRef(null)
  const zoomListenerRef = React.useRef(null)

  React.useEffect(() => {
    activeItemRef.current = activeItem
  }, [activeItem])

  const hideOverlay = React.useCallback(() => {
    if (!overlayRef.current) return
    const { div } = overlayRef.current
    if (div) {
      div.style.display = 'none'
    }
  }, [])

  const showOverlayForItem = React.useCallback((item, position) => {
    const gmap = mapRef.current
    const gmaps = gmapsRef.current
    if (!gmap || !gmaps || !item || !position) return

    // Lazily create OverlayView + DOM node
    if (!overlayRef.current) {
      const overlayDiv = document.createElement('div')
      overlayDiv.style.position = 'absolute'
      overlayDiv.style.padding = '8px 15px'
      overlayDiv.style.borderRadius = '999px'
      overlayDiv.style.background =
        theme.palette.presidio.color.BAKER_BEACH_WHITE
      overlayDiv.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.2)'
      overlayDiv.style.fontFamily = FONTS.MANDREL
      overlayDiv.style.fontSize = '24px'
      overlayDiv.style.fontWeight = '600'
      overlayDiv.style.color = theme.palette.primary.dark
      overlayDiv.style.whiteSpace = 'nowrap'
      overlayDiv.style.transform = 'translate(-50%, 0)'
      overlayDiv.style.pointerEvents = 'none'
      overlayDiv.style.display = 'none'

      const overlay = new gmaps.OverlayView()

      overlay.onAdd = () => {
        const panes = overlay.getPanes()
        if (panes && panes.floatPane) {
          panes.floatPane.appendChild(overlayDiv)
        }
      }

      overlay.draw = () => {
        if (!overlay.position) return
        const projection = overlay.getProjection()
        if (!projection) return
        const point = projection.fromLatLngToDivPixel(overlay.position)
        if (!point) return

        overlayDiv.style.left = `${point.x}px`

        // Approximate marker height and add a 10px gap above the pin.
        const PIN_HEIGHT = 120
        const GAP_ABOVE_PIN = 10
        overlayDiv.style.top = `${point.y - (PIN_HEIGHT + GAP_ABOVE_PIN)}px`
      }

      overlay.onRemove = () => {
        if (overlayDiv.parentNode) {
          overlayDiv.parentNode.removeChild(overlayDiv)
        }
      }

      overlay.setMap(gmap)
      overlayRef.current = { overlay, div: overlayDiv }
    }

    const { overlay, div } = overlayRef.current

    const text = item.location_name || item.card_title || ''
    div.textContent = text

    // store position on overlay instance
    const latLng =
      position instanceof gmaps.LatLng
        ? position
        : new gmaps.LatLng(position.lat, position.lng)
    overlay.position = latLng

    div.style.display = 'block'
    overlay.draw()
  }, [])

  // === NEW: shared marker hover helpers ===
  const highlightMarkerForItem = React.useCallback(
    (item) => {
      const activeIcon = markerIconActiveRef.current
      if (!activeIcon) return

      const entry = markersRef.current.find(
        ({ item: markerItem }) =>
          String(markerItem?.card_title) === String(item?.card_title)
      )
      if (!entry) return

      const { marker } = entry
      if (marker?.setIcon) {
        marker.setIcon(activeIcon)
      }

      const pos = marker.getPosition()
      if (pos) {
        showOverlayForItem(item, pos)
      }
    },
    [showOverlayForItem]
  )

  const clearHighlightForItem = React.useCallback(
    (item) => {
      const defaultIcon = markerIconDefaultRef.current
      if (!defaultIcon) return

      const entry = markersRef.current.find(
        ({ item: markerItem }) =>
          String(markerItem?.card_title) === String(item?.card_title)
      )
      if (!entry) return

      const { marker } = entry

      const isActive =
        activeItemRef.current &&
        String(activeItemRef.current?.card_title) === String(item?.card_title)

      if (!isActive && marker?.setIcon) {
        marker.setIcon(defaultIcon)
      }

      if (!isActive) {
        hideOverlay()
      } else {
        // Ensure overlay remains visible for the active item.
        const pos = marker.getPosition()
        if (pos) {
          showOverlayForItem(activeItemRef.current, pos)
        }
      }
    },
    [hideOverlay, showOverlayForItem]
  )
  // === /NEW ===

  const openPanel = React.useCallback((item, evt) => {
    if (evt?.currentTarget) {
      lastTriggerRef.current = evt.currentTarget
    }
    setActiveItem(item)
  }, [])

  const closePanel = React.useCallback(() => {
    setActiveItem(null)

    if (lastTriggerRef.current?.focus) {
      lastTriggerRef.current.focus()
    }

    hideOverlay()

    const gmap = mapRef.current
    const gmaps = gmapsRef.current
    if (gmap && gmaps && initialBoundsRef.current) {
      gmap.fitBounds(initialBoundsRef.current)
      if (gmaps.event?.addListenerOnce) {
        gmaps.event.addListenerOnce(gmap, 'bounds_changed', () => {
          if (gmap.getZoom() < minZoom) {
            gmap.setZoom(minZoom)
          }
        })
      } else if (gmap.getZoom() < minZoom) {
        gmap.setZoom(minZoom)
      }
    }
  }, [hideOverlay, minZoom])

  React.useEffect(() => {
    if (!activeItem) return
    const onKey = (e) => {
      if (e.key === 'Escape') closePanel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [activeItem, closePanel])

  // ===== Helpers (CTA, Image) =====
  const getCta = (item) => {
    const cta = item?.card_cta
    if (!cta || typeof cta === 'string') return null
    const { url, title, name, target } = cta
    if (!url) return null
    return {
      url,
      label: title || name || 'Learn more',
      target: target ? String(target) : undefined,
      external: isUrlExternal(url),
    }
  }

  const getImageUrl = (item) => {
    if (item?.card_image && typeof item.card_image === 'object') {
      return item.card_image.url || item.card_image.src || null
    }
    if (item?.card_image_url) return item.card_image_url
    return null
  }

  // ===== Measurement: keep textBox min-height constant =====
  const textBoxRef = React.useRef(null)
  const normalContentRef = React.useRef(null)
  const measureRef = React.useRef(null)

  const [textBoxWidth, setTextBoxWidth] = React.useState(0)
  const [normalHeight, setNormalHeight] = React.useState(0)
  const [tallestOverlayHeight, setTallestOverlayHeight] = React.useState(0)
  const allItems = React.useMemo(() => {
    if (!Array.isArray(listSections)) return []
    return listSections.flatMap((s) =>
      Array.isArray(s?.section_items) ? s.section_items : []
    )
  }, [listSections])

  React.useEffect(() => {
    if (!textBoxRef.current) return undefined
    const node = textBoxRef.current
    const ro = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const w = entry.contentRect?.width || node.offsetWidth
        setTextBoxWidth(w)
      })
    })
    ro.observe(node)
    setTextBoxWidth(node.offsetWidth)
    return () => {
      ro.disconnect()
    }
  }, [])

  React.useEffect(() => {
    if (!normalContentRef.current) return undefined
    const node = normalContentRef.current
    const update = () => setNormalHeight(node.scrollHeight)
    const ro = new ResizeObserver(update)
    ro.observe(node)
    update()
    const timer = setTimeout(update, 0)
    return () => {
      ro.disconnect()
      clearTimeout(timer)
    }
  }, [textBoxWidth, listSections, heading, subHeading])

  React.useEffect(() => {
    if (!measureRef.current) return undefined
    const t = requestAnimationFrame(() => {
      const nodes = measureRef.current.querySelectorAll(
        '[data-measure-item="true"]'
      )
      let maxH = 0
      nodes.forEach((n) => {
        const h = n.scrollHeight
        if (h > maxH) maxH = h
      })
      setTallestOverlayHeight(maxH)
    })
    return () => cancelAnimationFrame(t)
  }, [textBoxWidth, allItems])

  const minHeightPx = Math.max(normalHeight, tallestOverlayHeight)
  const textBoxStyle = minHeightPx
    ? { minHeight: `${minHeightPx}px` }
    : undefined
  const overlayTitleId = 'text-and-map-inline-overlay-title'

  /* =========================
     Map integration (classic API)
     ========================= */

  // Build geo items (parse lat/lng)
  const geoItems = React.useMemo(
    () =>
      (allItems || [])
        .map((it) => {
          const latNum = Number.parseFloat(it?.location_latitude)
          const lngNum = Number.parseFloat(it?.location_longitude)
          if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
            return { ...it, latNum, lngNum }
          }
          return null
        })
        .filter((item) => item !== null),
    [allItems]
  )

  // Initialize / update map + markers using classic constructors
  React.useEffect(() => {
    let cancelled = false

    ;(async () => {
      if (!mapDivRef.current || !geoItems.length) return

      const gmaps = await loadGoogleMaps()
      if (!gmaps || cancelled) return

      gmapsRef.current = gmaps

      const { Map, Marker, LatLngBounds, Size } = gmaps

      if (!Map) {
        // eslint-disable-next-line no-console
        console.error('Google Maps: Map constructor is not available.')
        return
      }

      // prepare icon configs using Google Size
      markerIconDefaultRef.current = {
        url: MARKER_ICON_DEFAULT_URL,
        scaledSize: Size ? new Size(60, 81) : undefined,
      }
      markerIconActiveRef.current = {
        url: MARKER_ICON_ACTIVE_URL,
        scaledSize: Size ? new Size(74, 100) : undefined,
      }

      // Create map once
      if (!mapRef.current) {
        const hasMapId = Boolean(GOOGLE_MAP_ID)

        const baseOptions = {
          center: { lat: geoItems[0].latNum, lng: geoItems[0].lngNum },
          zoom: 13,
          minZoom,
          maxZoom: MAP_MAX_ZOOM,
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'greedy',
        }

        baseOptions.mapId = GOOGLE_MAP_ID

        mapRef.current = new Map(mapDivRef.current, baseOptions)

        // 🔒 Enforce zoom limits even on user interaction.
        if (gmaps.event?.addListener) {
          if (zoomListenerRef.current) {
            gmaps.event.removeListener(zoomListenerRef.current)
          }
          zoomListenerRef.current = gmaps.event.addListener(
            mapRef.current,
            'zoom_changed',
            () => {
              const currentZoom = mapRef.current.getZoom()
              if (currentZoom < minZoom) {
                mapRef.current.setZoom(minZoom)
              } else if (currentZoom > MAP_MAX_ZOOM) {
                mapRef.current.setZoom(MAP_MAX_ZOOM)
              }
            }
          )
        }
      } else {
        // Keep options in sync if component re-renders.
        const hasMapId = Boolean(GOOGLE_MAP_ID)

        const options = {
          minZoom,
          maxZoom: MAP_MAX_ZOOM,
        }

        if (hasMapId) {
          options.styles = null
          options.mapId = GOOGLE_MAP_ID
        }

        if (Object.keys(options).length > 0 && mapRef.current.setOptions) {
          mapRef.current.setOptions(options)
        }
      }

      // Also refresh the zoom_changed listener so it uses the latest minZoom.
      if (gmaps.event?.addListener) {
        if (zoomListenerRef.current) {
          gmaps.event.removeListener(zoomListenerRef.current)
        }
        zoomListenerRef.current = gmaps.event.addListener(
          mapRef.current,
          'zoom_changed',
          () => {
            const currentZoom = mapRef.current.getZoom()
            if (currentZoom < minZoom) {
              mapRef.current.setZoom(minZoom)
            } else if (currentZoom > MAP_MAX_ZOOM) {
              mapRef.current.setZoom(MAP_MAX_ZOOM)
            }
          }
        )
      }

      // Clear existing markers
      markersRef.current.forEach(({ marker }) => {
        if (marker?.setMap) {
          marker.setMap(null)
        }
      })
      markersRef.current = []

      const defaultIcon = markerIconDefaultRef.current

      // Add markers
      geoItems.forEach((item) => {
        const marker = new Marker({
          map: mapRef.current,
          position: { lat: item.latNum, lng: item.lngNum },
          title: item?.card_title || '',
          icon: defaultIcon || undefined,
        })

        // Click opens the side panel (with zoom/pan handled in effect on activeItem)
        marker.addListener('click', () => openPanel(item))

        // Hover: use shared helpers
        marker.addListener('mouseover', () => {
          highlightMarkerForItem(item)
        })

        marker.addListener('mouseout', () => {
          clearHighlightForItem(item)
        })

        markersRef.current.push({ marker, item })
      })

      // Fit bounds to markers while honoring minZoom
      if (markersRef.current.length > 1 && LatLngBounds) {
        const bounds = new LatLngBounds()
        markersRef.current.forEach(({ item }) =>
          bounds.extend({ lat: item.latNum, lng: item.lngNum })
        )
        initialBoundsRef.current = bounds

        // ----- Dashed red boundary (like Google Maps place outline) -----
        if (boundaryPolylineRef.current) {
          boundaryPolylineRef.current.setMap(null)
          boundaryPolylineRef.current = null
        }

        if (
          Array.isArray(DASHED_BOUNDARY_PATH) &&
          DASHED_BOUNDARY_PATH.length > 1
        ) {
          const lineSymbol = {
            path: 'M -2.5,-2 L 2.5,-2 L 2.5,2 L -2.5,2 Z',
            fillColor: '#CD4F30', // dash color
            fillOpacity: 1,
            strokeOpacity: 0, // no stroke, just fill
            scale: 1, // thickness / size of each dash
          }
          const boundaryPolyline = new gmaps.Polyline({
            map: mapRef.current,
            path: DASHED_BOUNDARY_PATH,
            strokeColor: '#CD4F30',
            strokeOpacity: 0,
            strokeWeight: 2,
            icons: [
              {
                icon: lineSymbol,
                offset: '0',
                repeat: '10px', // spacing between dashes
              },
            ],
          })

          boundaryPolylineRef.current = boundaryPolyline
        }

        mapRef.current.fitBounds(bounds)

        if (gmaps.event?.addListenerOnce) {
          gmaps.event.addListenerOnce(mapRef.current, 'bounds_changed', () => {
            if (mapRef.current && mapRef.current.getZoom() < minZoom) {
              mapRef.current.setZoom(minZoom)
            }
          })
        } else if (mapRef.current.getZoom() < minZoom) {
          mapRef.current.setZoom(minZoom)
        }
      } else {
        initialBoundsRef.current = null
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    geoItems,
    openPanel,
    showOverlayForItem,
    hideOverlay,
    highlightMarkerForItem,
    clearHighlightForItem,
    minZoom,
  ])

  // When overlay opens / activeItem changes, update marker icons
  React.useEffect(() => {
    const defaultIcon = markerIconDefaultRef.current
    const activeIcon = markerIconActiveRef.current
    if (!defaultIcon || !activeIcon) return

    markersRef.current.forEach(({ marker, item }) => {
      const isActive =
        activeItem &&
        String(activeItem?.card_title) === String(item?.card_title)

      if (marker?.setIcon) {
        marker.setIcon(isActive ? activeIcon : defaultIcon)
      }
    })
  }, [activeItem])

  // When overlay opens, pan/zoom + show pill over the active marker
  React.useEffect(() => {
    const gmap = mapRef.current
    const gmaps = gmapsRef.current
    if (!gmap || !gmaps || !activeItem) return

    const lat = Number.parseFloat(activeItem?.location_latitude)
    const lng = Number.parseFloat(activeItem?.location_longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return

    gmap.panTo({ lat, lng })
    if (gmap.getZoom() < 15) gmap.setZoom(15)

    const found = markersRef.current.find(
      ({ item }) => String(item?.card_title) === String(activeItem?.card_title)
    )

    if (!found) return

    const pos = found.marker.getPosition()
    if (pos) {
      showOverlayForItem(activeItem, pos)
    }
  }, [activeItem, showOverlayForItem])

  return (
    <Grid
      className={`module ${classes.container}`}
      id={section_id}
      data-id="section"
      container
    >
      <Grid
        className={`${classes.textBox} ${classes.paddedBox}`}
        xs={12}
        lg={textPart}
        item
        ref={textBoxRef}
        style={isDesktop ? textBoxStyle : undefined}
      >
        {/* Normal content (hidden when overlay is active) */}
        {!activeItem && (
          <div ref={normalContentRef}>
            <Typography
              component={tagSelection}
              variant={styleSelection}
              className={classes.heading}
            >
              {heading}
            </Typography>

            {subHeading && (
              <Typography
                className={classes.subHeading}
                variant="h3"
                component="div"
              >
                {sanitize(subHeading)}
              </Typography>
            )}

            <Grid className={classes.listItemContainer}>
              {Array.isArray(listSections) && listSections.length > 0 && (
                <>
                  {listSections.map((section, sIdx) => (
                    <Box
                      key={`section-${sIdx}`}
                      className={classes.singleSection}
                      component="div"
                    >
                      {section?.section_heading?.trim() && (
                        <Typography
                          className={classes.listItemHeading}
                          component="div"
                        >
                          {sanitize(section.section_heading)}
                        </Typography>
                      )}

                      {Array.isArray(section?.section_items) &&
                        section.section_items.length > 0 && (
                          <Box className={classes.listItemBox} component="ul">
                            {section.section_items.map((item, iIdx) => (
                              <li
                                key={`section-${sIdx}-item-${iIdx}`}
                                className={classes.listItemLi}
                              >
                                <a
                                  href="#"
                                  className={classes.listItemButton}
                                  onClick={(e) => {
                                    e.preventDefault() // prevent page jump to "#"
                                    openPanel(item, e)
                                  }}
                                  onMouseEnter={() =>
                                    highlightMarkerForItem(item)
                                  }
                                  onMouseLeave={() =>
                                    clearHighlightForItem(item)
                                  }
                                  role="button"
                                  aria-haspopup="dialog"
                                  aria-controls={overlayTitleId}
                                  onKeyDown={(e) => {
                                    // Make Space behave like a button for keyboard users.
                                    if (e.key === ' ') {
                                      e.preventDefault()
                                      openPanel(item, e)
                                    }
                                  }}
                                >
                                  {sanitize(item?.card_title || '')}
                                </a>
                              </li>
                            ))}
                          </Box>
                        )}
                    </Box>
                  ))}
                </>
              )}
            </Grid>
          </div>
        )}

        {/* Inline overlay content (replaces normal content) */}
        {activeItem && (
          <Box
            role="dialog"
            aria-modal="false"
            aria-labelledby={overlayTitleId}
            className={classes.cardDialog}
          >
            <BackLink
              onClick={closePanel}
              ariaLabel="Back"
              className={classes.backLink}
            >
              {sanitize(backBtn) || 'Back to projects'}
            </BackLink>

            <Box className={classes.cardBox}>
              <Typography
                id={overlayTitleId}
                variant="h3"
                component="h3"
                className={classes.cardTitle}
              >
                {sanitize(activeItem.card_title || '')}
              </Typography>

              {(() => {
                const img = getImageUrl(activeItem)
                if (!img) return null
                return (
                  <Image
                    src={img}
                    alt={
                      activeItem.card_title
                        ? sanitize(activeItem.card_title)
                        : ''
                    }
                    width={1200}
                    height={675}
                    className={classes.cardImage}
                  />
                )
              })()}

              {activeItem?.card_description && (
                <Typography
                  variant="body1"
                  component="div"
                  className={classes.cardDescription}
                >
                  {sanitize(activeItem.card_description)}
                </Typography>
              )}

              {(() => {
                const cta = getCta(activeItem)
                if (!cta) return null
                return (
                  <a
                    href={cta.url}
                    onClick={(e) => {
                      e.preventDefault()
                      linkClickHandler(cta)
                    }}
                    data-ga-location="text_map_cta"
                    className={classes.cardCta}
                  >
                    <Button
                      className={classes.ctaButton}
                      variant={buttonStyle}
                      tabIndex={-1}
                      endIcon={
                        isUrlExternal(cta?.url) ? <ButtonExternalLink /> : null
                      }
                    >
                      {cta?.title || 'Learn more'}
                    </Button>
                  </a>
                )
              })()}
            </Box>
          </Box>
        )}
      </Grid>

      {/* Map column (right on large screens) */}
      <Grid xs={12} lg={mediaPart} item>
        <Box
          className={classes.mapBox}
          ref={mapDivRef}
          sx={{
            width: '100%',
            minHeight: isDesktop ? textBoxStyle?.minHeight || 400 : 400,
          }}
        />
      </Grid>

      {/* Hidden measurement area (matches textBox width) */}
      <div
        ref={measureRef}
        className={classes.measureRoot}
        aria-hidden="true"
        style={{ width: textBoxWidth || undefined }}
      >
        {allItems.map((mItem, idx) => (
          <div key={`measure-${idx}`} data-measure-item="true">
            {/* Mirror BackLink height for accurate measurement */}
            <button type="button" className={classes.backLink}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                aria-hidden="true"
              >
                <path d="M9.17475 3.34088C9.50425 3.67038 9.50425 4.20462 9.17475 4.53412L4.70887 9L9.17475 13.4659C9.50425 13.7954 9.50425 14.3296 9.17475 14.6591C8.84524 14.9886 8.31101 14.9886 7.9815 14.6591L2.919 9.59662C2.5895 9.26712 2.5895 8.73288 2.919 8.40338L7.9815 3.34088C8.31101 3.01137 8.84524 3.01137 9.17475 3.34088Z" />
                <path d="M3.375 9C3.375 8.53401 3.75276 8.15625 4.21875 8.15625H14.4844C14.9504 8.15625 15.3281 8.53401 15.3281 9C15.3281 9.46599 14.9504 9.84375 14.4844 9.84375H4.21875C3.75276 9.84375 3.375 9.46599 3.375 9Z" />
              </svg>
              <span>{sanitize(backBtn) || 'Back to projects'}</span>
            </button>

            <Typography variant="h4" component="h2">
              {sanitize(mItem?.card_title || '')}
            </Typography>

            {(() => {
              const img = getImageUrl(mItem)
              if (!img) return null
              return (
                <Image
                  src={img}
                  alt={mItem?.card_title ? sanitize(mItem.card_title) : ''}
                  width={1200}
                  height={675}
                />
              )
            })()}

            {mItem?.card_description && (
              <Typography variant="body1" component="div">
                {sanitize(mItem.card_description)}
              </Typography>
            )}

            {(() => {
              const cta = getCta(mItem)
              if (!cta) return null
              return <a href={cta.url}>{sanitize(cta.label)}</a>
            })()}
          </div>
        ))}
      </div>
    </Grid>
  )
}

TextAndMap.propTypes = {
  data: PropTypes.shape({
    section_title: PropTypes.string,
    section_id: PropTypes.string,
    text_map: PropTypes.shape({
      ctaHeading: PropTypes.string.isRequired,
      tag_selection: PropTypes.oneOf(['h2', 'h3']),
      style_selection: PropTypes.oneOf(['h2', 'h3']),
      ctaSubheading: PropTypes.string,
      list_sections: PropTypes.arrayOf(
        PropTypes.shape({
          section_heading: PropTypes.string,
          section_items: PropTypes.arrayOf(
            PropTypes.shape({
              location_name: PropTypes.string,
              location_latitude: PropTypes.string,
              location_longitude: PropTypes.string,
              card_title: PropTypes.string,
              card_image: PropTypes.oneOfType([
                PropTypes.bool,
                PropTypes.shape({
                  url: PropTypes.string,
                  src: PropTypes.string,
                }),
              ]),
              card_image_url: PropTypes.string,
              card_description: PropTypes.string,
              card_cta: PropTypes.oneOfType([
                PropTypes.shape({
                  type: PropTypes.string,
                  value: PropTypes.string,
                  url: PropTypes.string,
                  name: PropTypes.string,
                  title: PropTypes.string,
                  target: PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.number,
                  ]),
                }),
                PropTypes.string,
              ]),
            })
          ),
        })
      ),
      back_button_text: PropTypes.string,
      media_position: PropTypes.oneOf(['left', 'right']),
      remove_bottom_padding: PropTypes.bool,
    }),
  }),
}
